import React, { useImperativeHandle, useRef, useState } from "react";
import { RCCGrid, RCCRefModalProps, RCCInput, RCCHookToast, RCCTooltip, RCCButton, RCCDropDownItem, RCCServicesApi } from "react-common-components";
import { IForwardPagination } from "react-common-components/dist/components/Pagination/interface";
import { useForm } from "react-hook-form";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import DropdownWithBackground from "../DropdownWithBackground";
import FiltersContainer from "../FiltersContainer";
import LovClassifications from "../Lov/Classifications";
import LovMaps from "../Lov/Maps";
import LovSectors from "../Lov/Sectors";
import LovUsers from "../Lov/Users";
import Modal from "../Modal";
import Pagination from "../Pagination";
import { completePermissionType } from "../Permissions/interface";
import Select from "../Select";
import { IIndicatorListFilters, IModalIndicatorsPermissionsRef } from "./interface";

const ModalIndicatorsPermissions: React.ForwardRefRenderFunction<IModalIndicatorsPermissionsRef> = (_, ref) => {
	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();

	const modalIndicatorsPermissionsRef = useRef<RCCRefModalProps>(null);
	const paginationRef = useRef<IForwardPagination>(null);

	const [subtitle, setSubtitle] = useState("");
	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState<IIndicatorListFilters>({filter:"",situation:"A" });
	const [aditionalParams, setAditionalParams] = useState<{idEfa004?: number; idEfa005?: number;}>();

	const { 
		control,
		reset 
	} = useForm();

	const { 
		handleSubmit : handleSubmitFilters, 
		register : registerFilters,
		control:controlFilters, 
		reset: resetFilters, 
		watch: watchFilters 
	} = useForm();

	const submitFilters = (data: any) => {
		setFilters(() => {
			const newState = {} as IIndicatorListFilters;
			newState.filter = data.filter;
			newState.situation = data.situation.value;
			if(data.map)
				newState.map = typeof data.map === "object" ? data.map?.value : data.map;
			if(data.classification)
				newState.classification = typeof data.classification === "object" ? data.classification?.value : data.classification;
			if(data.sector)
				newState.sector = typeof data.sector === "object" ? data.sector?.value : data.sector;
			if(data.responsible)
				newState.responsible = typeof data.responsible === "object" ? data.responsible?.value : data.responsible;

			return newState;
		});
		paginationRef.current?.reload();
	};

	const openModal = (params: any, subtitleX: string) => {
		reset();
		setSubtitle(subtitleX);
		setAditionalParams(params);

		resetFilters();
		setFilters({filter:"",situation:"A" });
		setShowFilters(false);
	
		modalIndicatorsPermissionsRef.current?.openModal();
	};

	useImperativeHandle(ref, () => ({
		openModal
	}));

	const options = [{
		value: completePermissionType.NO_PERMISSION,
		label: translate("Sem Permissão")
	},
	{
		value: completePermissionType.VIEW,
		label: translate("Visualiza")
	},
	{
		value: completePermissionType.VIEW_AND_ANALIZE,
		label: translate("Visualiza e analisa")
	},
	{
		value: completePermissionType.VIEW_AND_CHANGE_VALUES,
		label: translate("Visualiza e altera valores")
	},
	{
		value: completePermissionType.VIEW_AND_CHANGE_VALUES_GOAL,
		label: translate("Visualiza e altera meta e valores")
	},
	{
		value: completePermissionType.VIEW_AND_EDIT,
		label: translate("Visualiza e edita")
	}];

	const colPermission = (row: any) => (
		<>
			<Select 
				name={`${row.iqc001Codigo}.permission`}
				control={control}
				label=""
				styleInline="height: 32px; min-height: 32px; min-width: 57px;" 
				options={options}	
				defaultValue={row.permission ? row.permission?.toString() : undefined}		
				onChangeCapture={(e) => {   
					if(e.value){
						savePermissions(Number(e.value), [row.iqc001Id]);
					}
				}}	
			/>
		</>
	); 

	const verifyFieldToSend = (field: any) => {
		if(typeof field === "string"){
			if(field !== "")
				return field;
			return null;
		}
		if(typeof field === "number")
			return field;
		return null;
		
	};

	const savePermissions = async (permission: number, idsInd?: number[], isDelete?: boolean) => {
		try{
			await RCCServicesApi.postRequest({
				method: "Indicators/SavePermissions",
				body: {
					...aditionalParams,
					idsInd: idsInd ?? [],
					permission,
					classification: verifyFieldToSend(filters.classification),
					filter: verifyFieldToSend(filters.filter),
					map: verifyFieldToSend(filters.map),
					responsible: verifyFieldToSend(filters.responsible),
					sector: verifyFieldToSend(filters.sector),
					situation: verifyFieldToSend(filters.situation)
				}
			});

			if(!idsInd || (idsInd && idsInd?.length > 1)){
				addToast({
					color: "green",
					description: isDelete ? translate("Permissões excluídas com sucesso") : translate("Permissões salvas com sucesso"),
					title: translate("Sucesso")
				});
				paginationRef.current?.reload();
			}else{
				addToast({
					color: "green",
					description: translate("Permissão salva com sucesso"),
					title: translate("Sucesso")
				});
			}
		

		}catch(err: any){
			const textMessageError = err?.response?.data?.Message;
			if(textMessageError === "specifyIndicators"){
				addToast({
					title: translate("Atenção"),
					description: translate("É necessário especificar os indicadores"),
					color: "yellow"
				});
				return;
			}
			standardErrorMessages(err, isDelete ? translate("Erro ao excluir permissões") : translate("Erro ao salvar as permissões"), addToast);
		}
	};

	const areUsingFilter = ():boolean => !!filters.classification || !!filters.filter || !!filters.responsible || !!filters.sector || !!filters.map|| filters.situation !== "T";

	return (
		<Modal
			ref={modalIndicatorsPermissionsRef}
			header={{
				title: translate("Permissões de Indicadores"),
				subtitle,
				icon: {
					name:  "lock"
				}
			}}
			footer={{
				cancelButtonText: translate("Cancelar"),
				showSaveButton: false
			}}
			withoutForm
		>
			<RCCGrid
				container
				item
				direction="column"
				alignItems="center"
			>
				<RCCGrid
					container
					item
					xs={12}
				>			
					<RCCGrid container item justifyContent="flex-end" alignItems="center">
						<RCCGrid 
							container 
							item 
							xs={6}
							justifyContent="flex-end"	
						>
							<DropdownWithBackground
								callerComponent={
									<RCCButton 
										color="purple"
										icon={{
											name: "arrowDown",
											size: 16
										}}
									>
										{translate("Aplicar para todos")}
									</RCCButton>
								}
							>
								<RCCDropDownItem
									selfEnclose
									onClick={() => savePermissions(Number(completePermissionType.NO_PERMISSION))}
								>
									{translate("Sem Permissão")}
								</RCCDropDownItem>
								<RCCDropDownItem
									selfEnclose
									onClick={() => savePermissions(Number(completePermissionType.VIEW))}
								>
									{translate("Visualiza")}
								</RCCDropDownItem>
								<RCCDropDownItem
									selfEnclose
									onClick={() => savePermissions(Number(completePermissionType.VIEW_AND_ANALIZE))}
								>
									{translate("Visualiza e analisa")}
								</RCCDropDownItem>
								<RCCDropDownItem
									selfEnclose
									onClick={() => savePermissions(Number(completePermissionType.VIEW_AND_CHANGE_VALUES))}
								>
									{translate("Visualiza e altera valores")}
								</RCCDropDownItem>
								<RCCDropDownItem
									selfEnclose
									onClick={() => savePermissions(Number(completePermissionType.VIEW_AND_CHANGE_VALUES_GOAL))}
								>
									{translate("Visualiza e altera meta e valores")}
								</RCCDropDownItem>
								<RCCDropDownItem
									selfEnclose
									onClick={() => savePermissions(Number(completePermissionType.VIEW_AND_EDIT))}
								>
									{translate("Visualiza e edita")}
								</RCCDropDownItem>
							</DropdownWithBackground>
							<RCCTooltip
								text={translate("Filtros")}
								color={showFilters || areUsingFilter() ? "blue" : "grey"}
								position="bottom"
							>
								<RCCButton
									icon={{name: "filter", color: "#FFF" }}
									color={showFilters || areUsingFilter() ? "blue" : "grey"}
									onClick={() => setShowFilters((prevState) => !prevState)}
								/>
							</RCCTooltip>
							<RCCTooltip
								color="red"
								text={translate("Excluir todas")}
								position="bottom"
							>
								<RCCButton 
									color="red"
									icon={{
										name: "trash"
									}}
									onClick={() => savePermissions(Number(completePermissionType.NO_PERMISSION), undefined, true)}
								/>
							</RCCTooltip>
						</RCCGrid>
					</RCCGrid>	

					<FiltersContainer
						onSubmitFilters={handleSubmitFilters(submitFilters)}
						resetFilters={
							() => resetFilters({
								filter: "",
								sector:{value:"",label:""},
								map:{value:"",label:""},
								responsible:{value:"",label:""},
								classification:{value:"",label:""},
								situation:{value: "A", label: translate("Ativo")}
							})}
						showFilters={showFilters}
					>
						<RCCGrid
							container
							item
						>
							<RCCGrid
								container
								item
								direction="row"
								spacing={1}
							>
								<RCCGrid
									container 
									item
									xs={12}
									sm={4}
									md={3}
								>
									<LovSectors
										control={controlFilters}
										name="sector"
										label={translate("Setor")}
										isClearable	
									/> 
								</RCCGrid>
								<RCCGrid
									container 
									item
									xs={12}
									sm={4}
									md={3}
								>
									<LovClassifications
										control={controlFilters}
										name="classification"
										label={translate("Classificação")}
										isClearable
									/> 
								</RCCGrid>
								<RCCGrid
									container 
									item
									xs={12}
									sm={4}
									md={3}
								>
									<LovMaps
										control={controlFilters}
										name="map"
										label={translate("Mapas")}
										isClearable
									/> 
								</RCCGrid>
								<RCCGrid
									container 
									item
									xs={12}
									sm={4}
									md={3}
								>
									<LovUsers
										control={controlFilters}
										name="responsible"
										label={translate("Responsável")}
										isClearable
									/>
								</RCCGrid>
								<RCCGrid
									container 
									item
									xs={12}
									sm={4}
									md={3}
								>
									<Select 
										control={controlFilters}
										name="situation" 
										options={[
											{value: "A", label: translate("Ativo")}, 
											{value: "I", label: translate("Inativo")},
											{value: "T", label: translate("Todos")}
										]} 
										label={translate("Situação")}
										defaultValue={{value: "A", label: translate("Ativo")}}
										submit="value and label"
									/>
								</RCCGrid>
								<RCCGrid
									container 
									item
									xs={12}
									sm={8}
									md={6}
									lg={6}
								>
									<RCCInput
										name="filter" 
										label={translate("Pesquisar")}
										register={registerFilters}
										defaultValue={watchFilters("filter")}
									/>
								</RCCGrid>
							</RCCGrid>
						</RCCGrid>
					</FiltersContainer>
				

					<Pagination
						ref={paginationRef}
						dataOrRoute="Indicators/ListPermissions"
						filtersData={{
							...filters,
							...aditionalParams
						}}
						styleInline={{
							ulContainer: "max-height: unset; overflow: visible;"
						}}
						columns={[
	  					{
								idColumn: "iqc001Codigo",
								nameColumn: translate("Código"),
								sm: 2,
								md: 1,
								type: "string"
							},
							{
								idColumn: "iqc001Descricao",
								nameColumn: translate("Descrição"),
								sm: 7,
								md: 8,
								type: "string"
							},
							{
								idColumn: "options",
								nameColumn: translate("Permissão"),
								justify: "left",
								sm: 3,
								md: 3,
								cellRenderer: colPermission,
								disableSort: true
							}
						]}
					/>
				</RCCGrid>
			</RCCGrid>
		</Modal>
	);
};

export default React.forwardRef(ModalIndicatorsPermissions);

